import { Box, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import PageviewIcon from '@mui/icons-material/Pageview';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DetailsWrapper from '../components/Details/DetailsWrapper';
import HorizontalWrapper from '../components/Details/HorizonticalWrapper';
import Theme from '../Themes/main.theme';
import HttpService from '../services/HttpService';
import UserService from '../services/UserService';
import { IntExt } from '../lib/permissions';
import { RequestDict, RequestStatus } from '../components/dictionaries/RequestStatus';
import { getAllUsers } from '../lib/static/users/fetchers';
import { useSnackbar } from 'notistack';
import DialogAction from '../components/Dialog/DialogAction';
import AsyncBackdropSubmit from '../components/AsyncBackdropSubmit';
import LinesSkeleton from '../components/Skeletons/LinesSkeleton';
import BackButton from '../components/Buttons/BackButton';

interface ISubmit {
  status?: string;
  responsible_user_id?: number | undefined | null;
}

interface IData {
  id: number; 
  title: string; 
  afm: string; 
  name: string | null;
  surname: string | null; 
  phone_number: string; 
  email: string; 
  dateFrom: string;
  dateTo: string;
  hours: {key: string, value: number}[];
  info: string;
  status: string;
  administrative_unit_id: null | number,
  responsible_user: {id: number, full_name: string};
  applicant_user: {id: number | null, full_name: string};
  protocol_number: number | null;
  submit_date: string;
  section: string;
  office: {id: number | null, category: string};
  administrative_unit: {id: number, title: string, region_id: number};
  applicant_role: string;
  location: {omeka_id: number, title: string};
  resp_details: { name: string | null, lastname: string | null, email: string, mobile: string}

}

const ViewLocationForm = () => {
  const [data, setData] = React.useState<IData>({ 
    id: 0,
    title: '',
    afm: '',
    dateFrom: '',
    dateTo: '',
    hours: [],
    info: '',
    name: '',
    surname: '',
    email: '',
    status: 'draft',
    phone_number: '',
    administrative_unit_id: null,
    administrative_unit: {id: 0, title: '', region_id: 0},
    responsible_user: {id: 0, full_name: ''},
    protocol_number: null,
    submit_date: '',
    section: '',
    applicant_user: {id: 0, full_name: ''},
    office: {id: 0, category: ''},
    applicant_role: '',
    location: {omeka_id: 0, title: ''},
    resp_details: { name: null, lastname: null, email: '', mobile: ''}
  });

  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const is_external = UserService.hasRole([IntExt.External]);
  const [users, setUsers] = React.useState<{key: string | null; 
    value: number | null | undefined | string}[]>([]);
  const [shouldRenderActionButtons, setShouldRenderActionButtons] = React.useState<boolean>(false);
  const [newUser, setNewUser] = React.useState<number | undefined | null>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const [open, setOpen] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  
  document.title = t('Προεπισκόπηση Αιτήματος Ενδιαφέροντος για Γύρισμα στην Τοποθεσία');

  const basic_info = [
    [{
      name: 'id',
      value: String(data.id)
    },
    {
      name: t('Αρμόδια Οργανική Μονάδα'),
      value: data.administrative_unit.id
        ? is_external
          ? String(data.administrative_unit.title)
          :<> <Stack direction='row'>
            <Typography>              
              {data.administrative_unit.title}
            </Typography><Tooltip PopperProps={{
              disablePortal: true
            }} title={t('Προβολή')}>
              <IconButton color='primary' size='small'
                onClick={() => { 
                  navigate(`/admin/units/${data.office.category}/${data.office.id}`);
                }}>
                <PageviewIcon/>
              </IconButton>
            </Tooltip>
          </Stack>
          </> 
        : '-'
    },
    {
      name: t('Τομέας'),
      value: data.section
    }],
    [{
      name: t('Αριθμός Πρωτοκόλλου'),
      value: data.protocol_number
        ? String(data.protocol_number)
        : '-'
    },
    { name: t('Υπεύθυνος Χειριστής'),
      value: <Box display='flex'>
        {data.responsible_user?.id
          ? <> 
            <Stack direction='row'>
              <Typography>              
                {data.responsible_user.full_name}              
              </Typography><Tooltip PopperProps={{
                disablePortal: true
              }} title={t('Προβολή')}>
                <IconButton color='primary' size='small'
                  onClick={() => { 
                    navigate(`/admin/users/${data.responsible_user?.id}`);
                  }}>
                  <PageviewIcon/>
                </IconButton>
              </Tooltip>
            </Stack>
          </> 
          : <Box mr={2}>-</Box>
        }
        <Tooltip
          PopperProps={{
            disablePortal: true
          }} 
          title={t('Αλλαγή Χρήστη')}>
          <IconButton color='primary' size='small'
            disabled={data?.status === RequestStatus.Completed
               || data?.status === RequestStatus.Successful}
            onClick={() => {
              setOpen(true);
            }}>
            <PersonIcon fontSize='inherit' />
          </IconButton >
        </Tooltip>
      </Box>
    },
    {
      name: t('Λογαριασμός Αιτούντος'),
      value: data?.applicant_user?.id
        ? <>
          <Stack direction='row'>
            <Typography>              
              {data?.applicant_user?.full_name}
            </Typography><Tooltip PopperProps={{
              disablePortal: true
            }} title={t('Προβολή')}>
              <IconButton color='primary' size='small'
                onClick={() => { 
                  pathname.split('/').filter(Boolean)[0] === 'admin'
                    ? data.applicant_role === IntExt.External
                      ? navigate(`/admin/visitor/${data?.applicant_user?.id}`)
                      : navigate(`/admin/users/${data?.applicant_user?.id}`)
                    : navigate(`/visitor/details/${data?.applicant_user?.id}`);
                }}>
                <PageviewIcon/>
              </IconButton>
            </Tooltip>
          </Stack>
        </>    
        : '-'
    }],
    [{
      name: t('Ημερομηνία Υποβολής'),
      value: moment(data.submit_date).format('DD/MM/YYYY')
    },
    {
      name: t('Κατάσταση'),
      value: RequestDict[data.status]
    }, 
    {
      name: t('Τοποθεσία'),
      value: data.location.omeka_id
        ? is_external
          ? String(data.location.title)
          :<> <Stack direction='row'>
            <Typography>              
              {data.location.title}
            </Typography><Tooltip PopperProps={{
              disablePortal: true
            }} title={t('Προβολή')}>
              <IconButton color='primary' size='small'
                onClick={() => { 
                  navigate(`/admin/locations/details/${data.location.omeka_id}`);
                }}>
                <PageviewIcon/>
              </IconButton>
            </Tooltip>
          </Stack>
          </> 
        : '-'
    }]
    
  ];

  const basic_info_details = [
    [{
      name: t('Επωνυμία Επαγγελματία / Επιχείρησης'),
      value: data?.title || '-'
    },
    {
      name: t('ΑΦΜ'),
      value: data?.afm || '-'
    }],
    [{
      name: t('Τηλέφωνο'),
      value: data?.phone_number || '-'
    },
    {
      name: t('Email'),
      value: data?.email || '-'
    }]
  ];

  const production_details = [
    {
      name: t('Ημερομηνία Από'),
      value: moment(data?.dateFrom).format('DD/MM/YYYY')   
    },
    {
      name: t('Ημερομηνία Έως'),
      value: moment(data?.dateTo).format('DD/MM/YYYY')   
    },
    {
      name: t('Ενδεικτικές Ώρες Γυρισμάτων'),
      value: data?.hours?.map(x => x.key)
    },
    {
      name: t('Διευκρινήσεις / Ερωτήσεις'),
      value: data?.info
    }
  ];

  const contact_info_details = [
    [{
      name: t('Όνομα'),
      value: data?.resp_details?.name || '-'
    },
    {
      name: t('Τηλέφωνο'),
      value: data?.resp_details.mobile || '-'
    }],
    [{
      name: t('Επώνυμο'),
      value: data?.resp_details.lastname || '-'
    },
    {
      name: t('Email'),
      value: data?.resp_details.email || '-'
    }]
  ];

  const getData = async () => {
    const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/applications/locations/filming/${id}`);    
    const response = await getAllUsers({
      administrative_unit_id: resp.data.administrative_unit.id,
      is_enabled: true
    });
    setUsers(response.data.map((user: any) => {
      return {
        key: user.full_name,
        value: user.id
      };
    }));
    try {
      setData(resp.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  }; 

  const SubmitValue = async ({ status, responsible_user_id }: ISubmit) => {
    try {
      status && setOpenBackdrop(true);
      const resp = await HttpService.getAxiosClient().put(`${process.env.REACT_APP_BACKEND_URL}/applications/locations/${id}`, {
        ...(responsible_user_id && {
          responsible_user_id: newUser
        })
      });      
      if (resp.status === 200) {
        if (responsible_user_id) {
          const user = users?.find((user) => user.value === responsible_user_id);
          setData({
            ...data as IData,
            responsible_user: {
              id: Number(user?.value),
              full_name: user?.key || ''
            }
          });
          setShouldRenderActionButtons(resp.data.is_responsible);
        }
        enqueueSnackbar(resp.data.message, { variant: resp.data.action });
        if (status) {
          if (pathname.split('/').filter(Boolean)[0] === 'admin') {
            navigate('/admin/applications/dashboard');
          } else navigate('/my-applications');
        }
      }
    } catch (error) {
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα'), { variant: 'error' });
      console.error(error);
    }
    setOpenBackdrop(false);
  };
  
  React.useEffect(() => {
    getData();
  }, [shouldRenderActionButtons]);

  return (
    <>
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        {t('Παρακαλώ περιμένετε καθώς επιβεβαιώνουμε τα στοιχεία σας')}
      </AsyncBackdropSubmit>
      <Box mt={2} mb={2}>
        <BackButton 
          onClick={() => pathname.split('/').filter(Boolean)[0] === 'admin'
            ? navigate('/admin/applications/dashboard')
            : navigate('/my-applications')
          }
        />
      </Box>
      <Typography variant='h2' color={Theme.labelColor?.main}>
        {t('Αίτημα Ενδιαφέροντος για Γύρισμα σε Τοποθεσία')}
      </Typography>
      <Box >
        <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
          <Typography variant='h3' color={Theme.labelColor?.main}>
            {t('Βασικές Πληροφορίες Αιτήματος')}       
          </Typography>
        </Box>
        {isLoading ?
          <LinesSkeleton lines={3}/> :
          <HorizontalWrapper 
            title = {''}
            details={basic_info}
            marginLeft />}
      </Box>
      <Box>
        <Box>
          <Box sx={{ backgroundColor: Theme.labelColor?.background, py: 1, pl: 1, ml: 1, my: 2 }}>
            <Typography variant='h3' color={Theme.labelColor?.main}>
              {t('Λεπτομέρειες Αιτήματος')}       
            </Typography>
          </Box>
          {isLoading ?
            <LinesSkeleton lines={7}/> : 
            <>
              <HorizontalWrapper 
                title = {'Βασικά Στοιχεία'}
                details={basic_info_details}
                marginLeft />
          
              <DetailsWrapper 
                title={t('Στοιχεία Γυρίσματος')}
                detailsProps={{ rows: production_details, 
                  removeLines: true,
                  renderArraysAsColumns: true }} 
                marginLeft
              />
              <HorizontalWrapper 
                title={t('Στοιχεία Υπεύθυνου Επικοινωνίας')}
                details={contact_info_details}
                marginLeft />
            </>}
        </Box>
      </Box>
      <DialogAction open={open} handleClose={() => {
        setOpen(false);
        setNewUser(data?.responsible_user.id);
      }}
      title={`${t('Αλλαγή ανάθεσης σε')}`}
      content = {users.length === 0 
        ? 'Δεν υπάρχουν διαθέσιμοι Ενεργοί Χρήστες στην συγκεκριμένη Οργανική Μονάδα'
        : <Select
          MenuProps={{
            disablePortal: true
          }}
          fullWidth
          value={newUser}
          onChange={(e) => setNewUser(e.target.value as number)}
        >
          {users.map((option: any) => {
            return (
              <MenuItem key={option.value} value={option.value}>{option.key}</MenuItem>
            );
          })}
        </Select>}
      onAccept={()=>{
        SubmitValue({ responsible_user_id: newUser });
        setOpen(false);
      }} />
    </>
  );
};

export default ViewLocationForm;